// cURL generator + shareable URL encoder.
// `toCurl` produces a copy-paste ready command (multi-line, single-quoted body).
// `toShareableUrl` encodes the form state into a URL-safe base64 hash fragment.

interface CurlInput {
    method: string;
    url: string;
    headers?: Record<string, string>;
    body?: unknown;
}

export function toCurl({ method, url, headers, body }: CurlInput): string {
    const lines: string[] = [`curl --request ${method.toUpperCase()} \\`];
    lines.push(`    --url '${url}' \\`);

    const headersOut = { ...(headers || {}) };
    const hasBody = body !== undefined && body !== null && method.toUpperCase() !== 'GET' && method.toUpperCase() !== 'DELETE';
    if (hasBody && !Object.keys(headersOut).some((h) => h.toLowerCase() === 'content-type')) {
        headersOut['Content-Type'] = 'application/json';
    }

    const headerKeys = Object.keys(headersOut);
    headerKeys.forEach((k, idx) => {
        const v = headersOut[k];
        const isLast = idx === headerKeys.length - 1 && !hasBody;
        lines.push(`    --header '${k}: ${v}'${isLast ? '' : ' \\'}`);
    });

    if (hasBody) {
        // single-quote-escape: ' -> '\''
        const bodyStr = typeof body === 'string' ? body : JSON.stringify(body, null, 2);
        const escaped = bodyStr.replace(/'/g, "'\\''");
        lines.push(`    --data '${escaped}'`);
    }

    return lines.join('\n');
}

// ---- Shareable URL ----

export interface ShareableState {
    operationId: string;
    pathParams?: Record<string, string>;
    queryParams?: Record<string, string>;
    body?: string; // raw body JSON string (preserves user formatting)
}

// URL-safe base64 (no padding) — works in browser via btoa/atob.
function b64UrlEncode(str: string): string {
    if (typeof btoa !== 'function') {
        // Node fallback (tests). Buffer is global in node env.
        const b = (globalThis as any).Buffer;
        if (b) return b.from(str, 'utf-8').toString('base64').replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
    // Convert UTF-8 → latin1 bytes for btoa
    const utf8 = unescape(encodeURIComponent(str));
    return btoa(utf8).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64UrlDecode(str: string): string {
    const padded = str.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((str.length + 3) % 4);
    if (typeof atob !== 'function') {
        const b = (globalThis as any).Buffer;
        if (b) return b.from(padded, 'base64').toString('utf-8');
    }
    return decodeURIComponent(escape(atob(padded)));
}

export function toShareableUrl({ operationId, formState }: { operationId: string; formState: Omit<ShareableState, 'operationId'> }): string {
    const payload: ShareableState = { operationId, ...formState };
    const encoded = b64UrlEncode(JSON.stringify(payload));
    const base = typeof window !== 'undefined' ? window.location.origin + window.location.pathname : '';
    return `${base}#try=${encoded}`;
}

export function decodeShareableHash(hash: string): ShareableState | null {
    const m = hash.match(/try=([^&]+)/);
    if (!m) return null;
    try {
        const raw = b64UrlDecode(m[1]);
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && typeof parsed.operationId === 'string') {
            return parsed as ShareableState;
        }
        return null;
    } catch {
        return null;
    }
}
